import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { clientAPI, productAPI, syncAPI } from "@/services/api";
import {
  CheckCircle2,
  Clock,
  Copy,
  Eye,
  EyeOff,
  Key,
  Link,
  Loader2,
  Megaphone,
  Package,
  Play,
  Plus,
  RefreshCw,
  Settings,
  Trash2,
  Zap,
} from "lucide-react";
import {
  TailAdminDataTable,
  TailAdminMetricCard,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const getProductId = (product) => product?.productId || product?.id || product?._id || "";
const getCommissionRate = (product) => Number(product?.commission_rate ?? product?.commission ?? 0);
const getSubscriptionAmount = (product) => Number(product?.subscription_amount ?? product?.subscriptionAmount ?? 0);
const getApiKey = (product) => product?.api_key || product?.apiKey || "";
const getStatus = (product) => product?.status || (product?.isActive ? "active" : "draft");

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const maskKey = (key) => (key ? `${key.slice(0, 6)}...${key.slice(-6)}` : "Not generated");

const ClientProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [showApiKey, setShowApiKey] = useState({});
  const [activeProductId, setActiveProductId] = useState(null);

  const [editApiEndpoint, setEditApiEndpoint] = useState("");
  const [editWebhookUrl, setEditWebhookUrl] = useState("");
  const [editCommissionRate, setEditCommissionRate] = useState("");
  const [editSubscriptionAmount, setEditSubscriptionAmount] = useState("");
  const [editIconUrl, setEditIconUrl] = useState("");
  const [editIconFile, setEditIconFile] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const [productsRes, profileRes] = await Promise.all([
        productAPI.getProducts(),
        clientAPI.getProfile(),
      ]);
      setProducts(productsRes.data || []);
      setActiveProductId(profileRes.data?.active_product_id || null);
    } catch {
      toast.error("Failed to load prove-requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const summary = useMemo(() => {
    const active = products.filter((product) => getStatus(product) === "active").length;
    const configured = products.filter((product) => product.api_endpoint).length;
    const avgCommission = products.length
      ? products.reduce((sum, product) => sum + getCommissionRate(product), 0) / products.length
      : 0;

    return {
      active,
      configured,
      avgCommission,
      monthlyValue: products.reduce((sum, product) => sum + getSubscriptionAmount(product), 0),
    };
  }, [products]);

  const handleOpenSettings = (product) => {
    setSelectedProduct(product);
    setEditApiEndpoint(product.api_endpoint || "");
    setEditWebhookUrl(product.webhook_url || "");
    setEditCommissionRate((getCommissionRate(product) * 100).toString());
    setEditSubscriptionAmount(getSubscriptionAmount(product).toString() || "5000");
    setEditIconUrl(product.iconUrl || product.icon_url || "");
    setEditIconFile(null);
    setShowSettingsDialog(true);
  };

  const handleSaveSettings = async () => {
    const productId = getProductId(selectedProduct);
    if (!productId) return;

    setSaving(true);
    try {
      await productAPI.updateProduct(productId, {
        api_endpoint: editApiEndpoint || null,
        webhook_url: editWebhookUrl || null,
        commission_rate: Number(editCommissionRate) / 100,
        subscription_amount: Number(editSubscriptionAmount),
        iconUrl: editIconUrl || null,
      });
      if (editIconFile) {
        await productAPI.uploadIcon(productId, editIconFile);
      }
      toast.success("Settings saved");
      setShowSettingsDialog(false);
      fetchProducts();
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateKey = async (productId) => {
    if (!productId || !window.confirm("This will invalidate the current API key. Continue?")) return;

    try {
      await productAPI.regenerateKey(productId);
      toast.success("API key regenerated");
      fetchProducts();
    } catch {
      toast.error("Failed to regenerate key");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!productId || !window.confirm("Are you sure you want to delete this prove-request?")) return;

    try {
      await productAPI.deleteProduct(productId);
      toast.success("Prove-request deleted");
      fetchProducts();
    } catch {
      toast.error("Failed to delete prove-request");
    }
  };

  const handleSetActive = async (productId) => {
    try {
      await clientAPI.setActiveProduct(productId);
      setActiveProductId(productId);
      toast.success("Active prove-request updated");
    } catch {
      toast.error("Failed to set active prove-request");
    }
  };

  const handleTriggerSync = async (productId) => {
    setSyncing(productId);
    try {
      await syncAPI.triggerSync(productId);
      toast.success("Sync triggered. Check back in a moment.");
    } catch {
      toast.error("Failed to trigger sync");
    } finally {
      setSyncing(null);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const productRows = products.map((product) => {
    const productId = getProductId(product);
    const apiKey = getApiKey(product);
    const isActive = activeProductId === productId;
    const configured = Boolean(product.api_endpoint);

    return {
      id: productId,
      cells: [
        <div>
          <div className="flex items-center gap-2">
            <p className="font-semibold text-gray-900">{product.name}</p>
            {isActive ? <TailAdminStatusBadge tone="primary">Active</TailAdminStatusBadge> : null}
          </div>
          {product.description ? (
            <p className="mt-1 max-w-sm text-xs leading-5 text-gray-500">{product.description}</p>
          ) : null}
        </div>,
        <TailAdminStatusBadge tone={getStatus(product) === "active" ? "success" : "neutral"}>
          {getStatus(product)}
        </TailAdminStatusBadge>,
        <span className="font-medium text-emerald-600">
          {(getCommissionRate(product) * 100).toFixed(0)}%
        </span>,
        <span className="font-medium text-gray-700">{formatCurrency(getSubscriptionAmount(product))}</span>,
        <div className="flex min-w-[210px] items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
          <code className="flex-1 truncate text-xs text-gray-500">
            {showApiKey[productId] ? apiKey : maskKey(apiKey)}
          </code>
          <button
            className="rounded-md p-1 text-gray-400 hover:bg-white hover:text-gray-700"
            onClick={() => setShowApiKey((current) => ({ ...current, [productId]: !current[productId] }))}
            aria-label="Toggle API key visibility"
          >
            {showApiKey[productId] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
          <button
            className="rounded-md p-1 text-gray-400 hover:bg-white hover:text-gray-700"
            onClick={() => copyToClipboard(apiKey, "API key")}
            aria-label="Copy API key"
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>,
        <span className={configured ? "inline-flex items-center gap-1 text-emerald-600" : "inline-flex items-center gap-1 text-gray-500"}>
          {configured ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
          {configured ? "Configured" : "Setup needed"}
        </span>,
        <div className="flex min-w-[300px] flex-wrap items-center gap-2">
          {!isActive ? (
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-lg"
              onClick={() => handleSetActive(productId)}
              data-testid={`set-active-${productId}`}
            >
              <Zap className="mr-1 h-4 w-4" />
              Active
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-lg"
            onClick={() => navigate(`/client/products/${productId}/campaigns`)}
            data-testid={`view-campaigns-${productId}`}
          >
            <Megaphone className="mr-1 h-4 w-4" />
            View Prove-Requests
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-lg"
            onClick={() => navigate(`/client/products/${productId}/integration`)}
          >
            <Link className="mr-1 h-4 w-4" />
            Integration
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-lg"
            onClick={() => handleOpenSettings(product)}
            data-testid={`settings-${productId}`}
          >
            <Settings className="mr-1 h-4 w-4" />
            Settings
          </Button>
          {configured ? (
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-lg"
              onClick={() => handleTriggerSync(productId)}
              disabled={syncing === productId}
              data-testid={`sync-${productId}`}
            >
              {syncing === productId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}
              Sync
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            onClick={() => handleDeleteProduct(productId)}
            data-testid={`delete-${productId}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>,
      ],
    };
  });

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow="Client workspace"
        title="Your Offers"
        description="Manage long-lived Offer setup, then launch funded Prove-Request pushes from each Offer."
        actions={
          <>
            <Button
              variant="outline"
              className="h-10 rounded-lg"
              onClick={fetchProducts}
              disabled={loading}
              data-testid="refresh-products-btn"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              className="h-10 rounded-lg"
              onClick={() => navigate("/client/prove-requests/create")}
              data-testid="create-product-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Offer
            </Button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <TailAdminMetricCard
          label="Total prove-requests"
          value={products.length}
          icon={Package}
          tone="primary"
          helper="Offers in workspace"
          loading={loading}
        />
        <TailAdminMetricCard
          label="Active offers"
          value={summary.active}
          icon={CheckCircle2}
          tone="success"
          helper="Available to BRMs"
          loading={loading}
        />
        <TailAdminMetricCard
          label="Integrated"
          value={`${summary.configured}/${products.length || 0}`}
          icon={Link}
          tone="warning"
          helper="API endpoints ready"
          loading={loading}
        />
        <TailAdminMetricCard
          label="Average reward"
          value={`${(summary.avgCommission * 100).toFixed(0)}%`}
          icon={Zap}
          tone="neutral"
          helper="Commission rate"
          loading={loading}
        />
      </div>

      <TailAdminPanel
        title="Offer inventory"
        description="Every Offer has its own integration and can contain multiple Prove-Request pushes."
        action={
          <Button variant="outline" size="sm" className="rounded-lg" onClick={() => navigate("/client/prove-requests/create")}>
            <Plus className="mr-2 h-4 w-4" />
            Create with setup
          </Button>
        }
      >
        {loading || products.length > 0 ? (
          <TailAdminDataTable
            loading={loading}
            columns={["Product", "Status", "Commission", "Subscription", "API key", "Integration", "Actions"]}
            rows={productRows}
          />
        ) : (
          <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 p-8 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
              <Package className="h-7 w-7" />
            </div>
            <h3 className="text-lg font-semibold text-gray-950">No Offers yet</h3>
            <p className="mt-2 max-w-md text-sm leading-6 text-gray-500">
              Create your first funded offer so BRMs can promote it and Proovam can verify subscriptions.
            </p>
            <Button className="mt-5 rounded-lg" onClick={() => navigate("/client/prove-requests/create")}>
              <Plus className="mr-2 h-4 w-4" />
              Create Offer
            </Button>
          </div>
        )}
      </TailAdminPanel>

      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Product Settings</DialogTitle>
            <DialogDescription>Configure API integration for {selectedProduct?.name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-icon-url">Product icon (PNG upload or URL)</Label>
              <div className="flex items-center gap-3">
                {(editIconFile || editIconUrl) ? (
                  <img
                    src={editIconFile ? URL.createObjectURL(editIconFile) : editIconUrl}
                    alt="Product icon preview"
                    className="h-12 w-12 rounded-xl border border-gray-200 object-cover"
                  />
                ) : null}
                <Input
                  id="edit-icon-url"
                  type="url"
                  placeholder="https://example.com/icon.png"
                  value={editIconUrl}
                  onChange={(event) => setEditIconUrl(event.target.value)}
                />
              </div>
              <Input
                type="file"
                accept="image/png"
                onChange={(event) => setEditIconFile(event.target.files?.[0] || null)}
              />
              <p className="text-xs text-gray-500">PNG only, maximum 512KB. SVG is rejected.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-commission">Commission (%)</Label>
                <Input
                  id="edit-commission"
                  type="number"
                  value={editCommissionRate}
                  onChange={(event) => setEditCommissionRate(event.target.value)}
                  min={1}
                  max={100}
                  data-testid="edit-commission-rate"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-subscription">Subscription (NGN)</Label>
                <Input
                  id="edit-subscription"
                  type="number"
                  value={editSubscriptionAmount}
                  onChange={(event) => setEditSubscriptionAmount(event.target.value)}
                  min={100}
                  data-testid="edit-subscription-amount"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="api-endpoint">
                <Link className="mr-1 inline h-4 w-4" />
                API Endpoint URL
              </Label>
              <Input
                id="api-endpoint"
                type="url"
                placeholder="https://api.yourcompany.com/enrollments"
                value={editApiEndpoint}
                onChange={(event) => setEditApiEndpoint(event.target.value)}
                data-testid="edit-api-endpoint"
              />
              <p className="text-xs text-gray-500">
                Proovam will call GET /check-enrollment?phone_hash=... to verify subscriptions.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="webhook-url">Webhook URL</Label>
              <Input
                id="webhook-url"
                type="url"
                placeholder="https://yourcompany.com/webhooks/proovam"
                value={editWebhookUrl}
                onChange={(event) => setEditWebhookUrl(event.target.value)}
                data-testid="edit-webhook-url"
              />
            </div>

            <div className="border-t border-gray-100 pt-4">
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg text-amber-600 hover:text-amber-700"
                onClick={() => handleRegenerateKey(getProductId(selectedProduct))}
              >
                <Key className="mr-2 h-4 w-4" />
                Regenerate API Key
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-product-settings">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientProducts;
